import { FC } from 'react';

/**
 * Modal decoration configuration
 */
export interface ModalDecoration {
	emoji: string;
	top?: string;
	right?: string;
	bottom?: string;
	left?: string;
	fontSize: string;
	opacity: number;
	rotation: number;
}

/**
 * A single semantic token value with light and dark mode variants.
 * Maps directly to Chakra's semantic token format.
 */
interface TokenValue {
	default: string;
	_dark: string;
}

/**
 * Semantic token overrides for seasonal themes.
 *
 * Each token provides { default, _dark } values. At render time,
 * resolveTokenOverrides() picks the correct value based on the
 * current hour and injects it as a CSS variable override.
 *
 * Only tokens that differ from the base/company theme need to be
 * listed here (primarily accent colors). All other semantic tokens
 * resolve natively through Chakra's data-theme attribute.
 */
export interface SeasonalTokenOverrides {
	colors: {
		primary: {
			fg: TokenValue;
			fgStrong: TokenValue;
			fgStronger: TokenValue;
			fgStrongest: TokenValue;
			bg: TokenValue;
			bgHover: TokenValue;
			bgActive: TokenValue;
			bgAlpha: TokenValue;
			bgAlphaHover: TokenValue;
			bgAlphaActive: TokenValue;
		};
		bg: {
			canvas: TokenValue;
			surface: TokenValue;
			soft: TokenValue;
			subtle: TokenValue;
			topNav: TokenValue;
			inset: TokenValue;
		};
		text: {
			interactive: TokenValue;
		};
		border: {
			default: TokenValue;
			soft: TokenValue;
		};
	};
}

/**
 * Helper to create a TokenValue where both modes use the same value.
 * Useful for force-dark themes (fall/winter) where a single palette applies.
 */
export const sameMode = (value: string): TokenValue => ({ default: value, _dark: value });

/**
 * Helper to create a TokenValue with distinct light and dark values.
 */
export const lightDark = (light: string, dark: string): TokenValue => ({ default: light, _dark: dark });

/**
 * Modal color configuration
 */
export interface ModalColors {
	overlayBg: string;
	contentBg: string;
	contentBgImage?: string;
	contentBgSize?: string;
	contentBgPosition?: string;
	contentBgRepeat?: string;
	borderColor: string;
	boxShadow: string;
	logoDropShadowColor: string;
	subtitleColor: string;
	enableButtonBg: string;
	enableButtonHoverBg: string;
	enableButtonActiveBg: string;
	enableButtonBoxShadow: string;
	enableButtonHoverBoxShadow: string;
	enableButtonTextColor?: string;
	cancelButtonColor: string;
	cancelButtonHoverColor: string;
	noticeBg: string;
	noticeBorderColor: string;
	noticeTextColor: string;
	logoOverride?: 'light' | 'dark';
	logoMaxWidth?: string;
	featureCardBg?: string;
	featureCardBorderColor?: string;
	featureCardTextColor?: string;
	closeButtonHoverBg?: string;

	/**
	 * Scenic variant only. Soft gradient strip rendered behind the logo.
	 * Falls back to no strip when omitted.
	 */
	headerAccentGradient?: string;

	/**
	 * Scenic variant only. Gradient bubble around each feature emoji.
	 * Falls back to featureCardBg when omitted.
	 */
	featureBubbleGradient?: string;
	featureBubbleBorderColor?: string;

	/**
	 * Scenic variant only. CSS gradient for the enable button background.
	 * Takes precedence over enableButtonBg when provided.
	 */
	enableButtonBgGradient?: string;
	enableButtonHoverBgGradient?: string;
}

/**
 * Modal feature description
 */
export interface ModalFeature {
	emoji: string;
	text: string;
	toggleKey?: string;
}

/**
 * Modal text content configuration
 */
export interface ModalText {
	subtitle: string;
	features: [ModalFeature, ModalFeature, ModalFeature];
	endDateNotice: string;
	enableButtonText: string;
	cancelButtonText: string;
}

/**
 * Color mode strategy for seasonal themes.
 * - 'force-dark': Always force dark mode (existing behavior for fall/winter)
 * - 'time-based': Use current time of day to pick light or dark mode
 */
export type ColorModeStrategy = 'force-dark' | 'time-based';

/**
 * Layout variant for the intro modal.
 * - 'classic' (default): original layout with feature cards
 * - 'scenic': polished layout with header accent strip, emoji bubbles,
 *   gradient CTA, and notice moved to footer. Suited to scenic themes
 *   (spring) where the modal should echo the live background.
 */
export type ModalVariant = 'classic' | 'scenic';

/**
 * Configuration for a seasonal avatar overlay.
 *
 * The overlay is rendered on top of every avatar when the seasonal theme
 * is enabled (and the user is not on an admin route). Positioning uses
 * pure CSS percentages, so the artwork stays pixel-perfect aligned at
 * every avatar size — from 32px navbar avatars to 96px profile pages.
 *
 * To align asymmetric artwork (e.g. a wreath whose ring sits above its
 * geometric center because petals hang below), set `anchor` to the
 * normalized point inside the artwork that should land on the avatar's
 * center.
 */
export interface AvatarOverlayConfig {
	/** Asset URL for the overlay artwork. Should have a transparent background. */
	image: string;
	/**
	 * Width of the rendered overlay as a fraction of the avatar's diameter.
	 * Height is derived from the asset's natural aspect ratio.
	 *
	 * @default 1.4 (overlay extends 40% beyond the avatar)
	 */
	scale?: number;
	/**
	 * Normalized anchor point inside the artwork (0–1, fractions of the
	 * artwork's width/height) that should sit on the avatar's center.
	 *
	 * Use this to compensate for asymmetric artwork. For example, the spring
	 * wreath's ring sits above its geometric center because flower petals
	 * hang below — its anchor is `{ x: 0.5, y: 0.44 }`.
	 *
	 * @default { x: 0.5, y: 0.5 } (centered)
	 */
	anchor?: { x: number; y: number };
}

/**
 * Complete configuration for a seasonal theme
 */
export interface SeasonConfig {
	themeClassName: string;
	toggleEmoji: string;
	/**
	 * When true, the toggle emoji is hidden on the modal action button.
	 * @default false
	 */
	hideEmojiOnButton?: boolean;

	logoLight: string;
	logoDark: string;

	modalSound: string;
	modalSoundVolume: number;

	/**
	 * Chakra semantic token overrides with { default, _dark } values.
	 * Passed to createTheme() and applied via a nested ChakraProvider.
	 */
	semanticTokenOverrides: SeasonalTokenOverrides;

	colorModeStrategy?: ColorModeStrategy;

	/** Optional layout variant for the intro modal; defaults to 'classic'. */
	modalVariant?: ModalVariant;

	buttonTexts: string[];
	modalDecorations: ModalDecoration[];
	modalColors: ModalColors;
	modalText: ModalText;
	EffectsComponent: FC;
	/**
	 * Optional season-specific CSS overrides (frosted panels, menu styling, etc.)
	 * appended to the shared seasonal CSS at runtime. Co-locating per-season CSS
	 * in the season's own directory keeps the shared overrides file from growing
	 * unbounded.
	 */
	themeCss?: string;
	/**
	 * Optional avatar overlay rendered on every avatar while the theme is
	 * active and the user is on a non-admin route.
	 */
	avatarOverlay?: AvatarOverlayConfig;
}
